if (!customElements.get('localization-form')) {
  customElements.define('localization-form', class LocalizationForm extends HTMLElement {
    constructor() {
      super();
      this.mql = window.matchMedia('(min-width: 750px)');
      this.header = document.querySelector('.header-wrapper');
      this.button = this.querySelector('button.localization-form__select');
      this.panel = this.querySelector('.disclosure__list-wrapper');
      this.input = this.querySelector('input[name="locale_code"], input[name="country_code"]');
      this.search = this.querySelector('input[name="country_filter"]');
      this.closeButton = this.querySelector('.country-selector__close-button');
      this.resetButton = this.querySelector('.country-filter__reset-button');
      this.searchIcon = this.querySelector('.country-filter__search-icon');
      this.liveRegion = this.querySelector('[data-country-search-live-region]');
      this.overlay = this.querySelector('.country-selector__overlay');
      this.form = this.querySelector('form');
      this.portal = null;
      if (!this.button || !this.panel || !this.input || !this.form) return;

      this.panel.querySelectorAll('.disclosure__link[data-value]').forEach((item) => item.addEventListener('click', (event) => this.selectItem(event)));
      this.button.addEventListener('click', (event) => { event.preventDefault(); this.toggle(); });
      this.closeButton?.addEventListener('click', (event) => { event.preventDefault(); this.close(true); });
      this.overlay?.addEventListener('click', () => this.close(true));
      this.addEventListener('keydown', (event) => this.onKeydown(event));
      document.addEventListener('keydown', (event) => { if (event.key === 'Escape' && this.isOpen) { event.preventDefault(); this.close(true); } });
      this.addEventListener('focusout', () => { if (!this.mql.matches) return; requestAnimationFrame(() => { if (!this.contains(document.activeElement)) this.close(false); }); });

      if (this.search) {
        this.search.addEventListener('input', () => this.filterCountries());
        this.search.addEventListener('keydown', (event) => { if (event.key === 'Enter') event.preventDefault(); });
        this.search.addEventListener('focus', () => this.searchIcon?.classList.add('country-filter__search-icon--hidden'));
        this.search.addEventListener('blur', () => { if (!this.search.value) this.searchIcon?.classList.remove('country-filter__search-icon--hidden'); });
      }
      this.resetButton?.addEventListener('click', (event) => { event.preventDefault(); if (!this.search) return; this.search.value = ''; this.filterCountries(); this.search.focus(); });
      this.mql.addEventListener?.('change', () => { if (this.isOpen) this.close(false); });
    }

    get isOpen() { return this.button?.getAttribute('aria-expanded') === 'true'; }
    toggle() { this.isOpen ? this.close(false) : this.open(); }

    createPortal() {
      if (this.mql.matches || this.portal || !this.panel || !this.panel.classList.contains('country-selector')) return;
      const panelPlaceholder = document.createComment('embrynium-localization-panel');
      this.panel.parentNode.insertBefore(panelPlaceholder, this.panel);
      let overlayPlaceholder = null;
      if (this.overlay) {
        overlayPlaceholder = document.createComment('embrynium-localization-overlay');
        this.overlay.parentNode.insertBefore(overlayPlaceholder, this.overlay);
      }
      this.portal = { panelPlaceholder, overlayPlaceholder };
      this.panel.classList.add('emb-localization-portal');
      document.body.appendChild(this.panel);
      if (this.overlay) { this.overlay.classList.add('emb-localization-overlay-portal'); document.body.appendChild(this.overlay); }
    }

    restorePortal() {
      if (!this.portal) return;
      this.panel.classList.remove('emb-localization-portal');
      this.portal.panelPlaceholder.parentNode?.insertBefore(this.panel, this.portal.panelPlaceholder);
      this.portal.panelPlaceholder.remove();
      if (this.overlay && this.portal.overlayPlaceholder) {
        this.overlay.classList.remove('emb-localization-overlay-portal');
        this.portal.overlayPlaceholder.parentNode?.insertBefore(this.overlay, this.portal.overlayPlaceholder);
        this.portal.overlayPlaceholder.remove();
      }
      this.portal = null;
    }

    open() {
      this.createPortal();
      this.panel.hidden = false;
      if (this.overlay) this.overlay.hidden = false;
      this.button.setAttribute('aria-expanded', 'true');
      if (this.portal) document.body.classList.add('emb-localization-open');
      if (this.hasAttribute('data-prevent-hide') && this.header) this.header.preventHide = true;
      if (this.search && this.mql.matches) requestAnimationFrame(() => this.search.focus());
    }

    close(restoreFocus = false) {
      this.panel.hidden = true;
      if (this.overlay) this.overlay.hidden = true;
      this.button.setAttribute('aria-expanded', 'false');
      document.body.classList.remove('emb-localization-open');
      if (this.header) this.header.preventHide = false;
      if (this.search) { this.search.value = ''; this.filterCountries(); }
      this.restorePortal();
      if (restoreFocus) this.button.focus();
    }

    selectItem(event) {
      event.preventDefault();
      const value = event.currentTarget?.dataset?.value;
      if (!value) return;
      this.input.value = value;
      if (typeof this.form.requestSubmit === 'function') this.form.requestSubmit(); else this.form.submit();
    }

    onKeydown(event) {
      if (!this.isOpen || !['ArrowDown', 'ArrowUp'].includes(event.key)) return;
      const links = [...this.panel.querySelectorAll('.disclosure__link[data-value]')].filter((link) => !link.closest('.hidden'));
      if (!links.length) return;
      event.preventDefault();
      let index = links.indexOf(document.activeElement);
      index = event.key === 'ArrowDown' ? (index + 1) % links.length : (index <= 0 ? links.length - 1 : index - 1);
      links[index].focus();
    }

    normalize(value) { return String(value || '').normalize('NFD').replace(/\p{Diacritic}/gu, '').toLowerCase(); }
    filterCountries() {
      if (!this.search) return;
      const query = this.normalize(this.search.value.trim());
      const links = [...this.panel.querySelectorAll('.disclosure__link[data-value]')];
      let visible = 0;
      this.panel.querySelector('.popular-countries')?.classList.toggle('hidden', Boolean(query));
      this.resetButton?.classList.toggle('hidden', !query);
      links.forEach((link) => {
        const country = this.normalize(link.querySelector('.country')?.textContent || link.textContent);
        const show = !query || country.includes(query);
        link.parentElement?.classList.toggle('hidden', !show);
        if (show) visible += 1;
      });
      if (this.liveRegion && window.accessibilityStrings?.countrySelectorSearchCount) this.liveRegion.textContent = window.accessibilityStrings.countrySelectorSearchCount.replace('[count]', visible);
      this.panel.querySelector('.country-selector__list')?.scrollTo?.({ top: 0 });
    }
  });
}
